import * as XLSX from 'xlsx-js-style'
import { Excel_Formats, Excel_Styles } from './useDownloadCustomerTypeTemplateStyles'
import { useProductLoaders } from "../../../../products"
import { usePricesHooks } from "../../../../operations"
import { useCustomerTypeLoaders } from "../../.."
import { CUSTOMER_TYPE_PRICE_LABEL, ImportCustomerTypeField } from "../../../../../features/importer"
import { CustomerType } from '../../..'
import { useSupplierLoaders } from '../../../../purchases'

export const useDownloadCustomerTypeTemplate = () => {
    const { product: priceProductHooks } = usePricesHooks()
    const productLoaderInitializer = useProductLoaders()
    const supplierLoaders = useSupplierLoaders()
    const { findAllCustomerTypes } = useCustomerTypeLoaders()()

    return async (priceField: ImportCustomerTypeField, baseListId?: string, currentCT?: CustomerType) => {
        const priceLabel = CUSTOMER_TYPE_PRICE_LABEL[priceField];
        const withVat = priceField === ImportCustomerTypeField.PRICE_VAT;
        const { loadAllProducts } = productLoaderInitializer()
        
        const [allProducts, allCustomerTypes] = await Promise.all([
            loadAllProducts(),
            findAllCustomerTypes()
        ])

        const baseList = allCustomerTypes.find(ct => ct.id === baseListId)
        const baseListName = baseList?.name || "Precio Base"
        const generalYield = currentCT ? currentCT.yield : 0;

        // Los excluidos de la lista (o de la base, si es nueva) no llevan precio
        const excludedIds = ((currentCT || baseList)?.productYields || [])
            .filter(py => py.isExcluded)
            .map(py => py.productId)

        // Los productos archivados no forman parte de las listas de precios
        const sortedProducts = allProducts
            .filter(p => !p.archived && !excludedIds.includes(p.id))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))

        // El proveedor es informativo: permite filtrar al cargar la lista de precios de un proveedor
        const supplierIds = Array.from(new Set(sortedProducts.map(p => p.supplierId).filter(id => !!id)))
        const suppliers = await supplierLoaders().loadSuppliers(supplierIds)
        const supplierNames = new Map(suppliers.map(s => [s.id, s.name]))

        const vatLabel = withVat ? 'c/IVA' : 's/IVA'
        // Al editar se muestra el precio actual de la lista; si es nueva, el de su lista base (o el del producto)
        const shownPriceLabel = currentCT ? 'Precio Actual' : (baseList ? 'Precio Lista Base' : 'Precio Base')
        const header = [
            "Código Interno",
            "Producto",
            "Proveedor",
            `${shownPriceLabel} (${vatLabel})`,
            `Nuevo Precio ${priceLabel}`
        ]

        const dataRows: any[] = [
            ["Nombre Lista", currentCT?.name || "Nueva Lista"],
            ["Lista Base", baseListName],
            ["Ajuste General", generalYield / 100],
            [],
            header
        ]

        const ws = XLSX.utils.aoa_to_sheet(dataRows);

        ['A1', 'A2', 'A3'].forEach(cell => ws[cell].s = Excel_Styles.metadataTitle);
        
        ws['B1'].s = Excel_Styles.editable;
        ws['B2'].s = Excel_Styles.greyRow;
        ws['B3'] = { 
            v: generalYield / 100, 
            t: 'n', 
            z: Excel_Formats.Percentage, 
            s: Excel_Styles.editable 
        };

        sortedProducts.forEach((p, idx) => {
            const rowIdx = idx + 5;
            // Mismo cálculo de precios que usa la importación
            const shownPrice = priceProductHooks.adjustedPrice(p.id, withVat, currentCT ? currentCT.id : baseListId);

            const rowData = [
                { v: p.supplierCode || "", t: 's', s: Excel_Styles.greyRow },
                { v: p.name || "", t: 's', s: Excel_Styles.greyRow },
                { v: supplierNames.get(p.supplierId) || "", t: 's', s: Excel_Styles.greyRow },
                { v: shownPrice, t: 'n', z: Excel_Formats.Accounting, s: Excel_Styles.greyRow },
                // Se completa solo para los productos cuyo precio se quiere cambiar
                { v: "", t: 's', z: Excel_Formats.Accounting, s: Excel_Styles.editable }
            ];

            XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: `A${rowIdx + 1}` });
        });

        header.forEach((_, C) => {
            const cellRef = XLSX.utils.encode_cell({ r: 4, c: C });
            if (ws[cellRef]) ws[cellRef].s = Excel_Styles.header;
        });

        ws['!cols'] = [{ wch: 15 }, { wch: 40 }, { wch: 25 }, { wch: 20 }, { wch: 25 }]

        // Autofiltro sobre los encabezados para poder filtrar por proveedor
        const lastRow = Math.max(4, sortedProducts.length + 4)
        ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: lastRow, c: header.length - 1 } }) }

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Importar")
        const fileName = currentCT 
            ? `Edicion Lista de Precios ${currentCT.name.replace(/\s+/g, '_')}.xlsx`
            : `Nueva Lista de Precios.xlsx`;
        XLSX.writeFile(wb, fileName);
    }
}